import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { CampaignCard } from "components/campaign-card";
import api, { isSuccessResponse } from "lib/api";
import {
  buildProjectsListUrl,
  mapProjectToCampaignCard,
  parseProjectsListResponse,
} from "lib/projects";

const featuredCampaignShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
});

function FeaturedCampaignsGrid({ campaigns }) {
  return (
    <div className="mx-auto flex w-full max-w-6xl flex-wrap items-stretch justify-center gap-6">
      {campaigns.map((campaign) => (
        <div
          key={campaign.id}
          className="w-full min-w-0 max-w-md sm:max-w-88 lg:max-w-96"
        >
          <CampaignCard campaign={campaign} className="h-full" />
        </div>
      ))}
    </div>
  );
}

function FeaturedCampaignsContent({ loading, error, campaigns }) {
  if (loading) {
    return (
      <p className="text-center text-sm text-muted-foreground">Loading projects…</p>
    );
  }

  if (error) {
    return <p className="text-center text-sm text-destructive">{error}</p>;
  }

  if (campaigns.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground">
        No projects available yet.
      </p>
    );
  }

  return <FeaturedCampaignsGrid campaigns={campaigns} />;
}

FeaturedCampaignsGrid.propTypes = {
  campaigns: PropTypes.arrayOf(featuredCampaignShape).isRequired,
};

FeaturedCampaignsContent.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  campaigns: PropTypes.arrayOf(featuredCampaignShape).isRequired,
};

export function FeaturedCampaigns({ cardsOnly = false }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const path = buildProjectsListUrl();
        const resp = await api.get(path, { auth: false });
        if (cancelled) return;
        if (!isSuccessResponse(resp)) {
          setCampaigns([]);
          setError(resp?.message || "Could not load projects");
          return;
        }
        const { items } = parseProjectsListResponse(resp?.data ?? resp);
        const mapped = items
          .map((p) => mapProjectToCampaignCard(p, { descriptionMaxLen: 50 }))
          .filter(Boolean);
        setCampaigns(mapped);
      } catch (e) {
        if (!cancelled) {
          setCampaigns([]);
          setError(e?.message || "Could not load projects");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        {cardsOnly ? null : (
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold tracking-tight">
              Featured Equity Crowdfunding Projects
            </h2>
          </div>
        )}

        <FeaturedCampaignsContent
          loading={loading}
          error={error}
          campaigns={campaigns}
        />
      </div>
    </section>
  );
}

FeaturedCampaigns.propTypes = {
  cardsOnly: PropTypes.bool,
};
